import type { MeFacts, MeResponse, NudgeState, Preferences } from "@lms/shared-types";
import type { Session } from "../auth/auth";

/**
 * Nudges (PLT-06; the in-app-nudges template, with the three extensions
 * templates-port-plan.md §6 builds here first): one banner at a time
 * asking the person to finish something. The registry is the list of asks
 * in priority order — compliance first — and each entry says when it
 * applies from facts the server computed (`/v1/me` `facts`) or, for the
 * passkey, the one fact only the browser can learn.
 *
 * What the person said lives in their preferences (`nudges[id]`), so it
 * follows them between devices and lands in the trail as a preference
 * change. "Do later" hides a nudge until the next sign-in — compared by the
 * sign-in's id, not by `auth_time`, so a step-up in between does not bring
 * it back (template pitfall 6). "Dismiss forever" exists only where the ask
 * may be refused: a compliance ask is `dismissible: false` and offers
 * "Do later" alone.
 *
 * The next entry, when agreements exist (AGR-*): `agreement-to-sign`,
 * `dismissible: false`, applying on `facts.agreementToSign`, pointing at the
 * agreement. It is the port plan's first nudge and is not here because
 * there is no agreement for anyone to sign yet.
 */
export interface NudgeFacts extends MeFacts {
  /** How many passkeys work on this site; undefined while unknown, so the passkey nudge waits rather than flashing. */
  passkeyCount?: number | undefined;
  /** WebAuthn is available in this browser. */
  passkeysPossible: boolean;
}

export interface Nudge {
  /** The key in `preferences.nudges`; never renamed, or every answer to it is lost. */
  id: string;
  title: string;
  body: (facts: NudgeFacts) => string;
  /** Where the primary action goes. */
  action: { label: string; to: string };
  /** False for an ask the person may put off but not refuse: no "Dismiss forever". */
  dismissible: boolean;
  applies: (me: MeResponse, facts: NudgeFacts) => boolean;
  /** Paths the nudge is quiet on — the page it points at, and any page that already says the same thing. */
  quietOn: readonly string[];
}

export const NUDGES: readonly Nudge[] = [
  {
    id: "training-late",
    title: "Training is late",
    body: (f) =>
      `${f.trainingLate === 1 ? "One of your courses was" : `${f.trainingLate} of your courses were`} wanted by now. Finish it, or tell your program director what is in the way.`,
    action: { label: "Go to My training", to: "/training" },
    // Overdue training is a record an auditor reads (CNT-32); it can wait a sign-in, not be switched off.
    dismissible: false,
    applies: (_me, f) => (f.trainingLate ?? 0) > 0,
    // Home's "What needs doing" already leads with the late course.
    quietOn: ["/", "/training"],
  },
  {
    id: "passkey",
    title: "Sign in with a touch",
    body: () => "A passkey signs you in with your fingerprint, face or security key — no password, no code — and confirms it's you with a touch. Enroll one on this device.",
    action: { label: "Enroll a passkey", to: "/account#security" },
    dismissible: true,
    // A federated person's passkeys are their identity provider's business (ADR-0014).
    applies: (me, f) => !me.identityProvider && f.passkeysPossible && f.passkeyCount === 0,
    quietOn: ["/account"],
  },
];

type SignIn = Pick<Session, "signedInAt">;

/** The id of this sign-in: set once at the callback, kept across step-ups. */
export function signInId(session: SignIn | undefined): number | null {
  return session?.signedInAt ?? null;
}

/** Whether the person's recorded answer still hides the nudge. */
export function snoozed(state: NudgeState | undefined, session: SignIn | undefined): boolean {
  if (!state) return false;
  if (state.state === "never") return true;
  // "later" names the sign-in it was said in; any other sign-in brings the nudge back — and so does a
  // "later" that names none (the server refuses one now; an old or hand-written row is not trusted).
  const current = signInId(session);
  return typeof state.signIn === "number" && current !== null && state.signIn === current;
}

/** What to store for an answer given now, in this sign-in. A "later" with no sign-in to name is refused, not stored. */
export function answer(state: NudgeState["state"], session: SignIn | undefined, now = new Date()): NudgeState {
  if (state === "never") return { state, at: now.toISOString() };
  const signIn = signInId(session);
  if (signIn === null) throw new Error("This sign-in has no id to put the reminder off against; sign out and in again.");
  return { state, at: now.toISOString(), signIn };
}

const quiet = (n: Nudge, pathname: string) => n.quietOn.some((p) => pathname === p || (p !== "/" && pathname.startsWith(`${p}/`)));

export function visibleNudge(me: MeResponse | undefined, preferences: Pick<Preferences, "nudges">, session: SignIn | undefined, facts: NudgeFacts, pathname: string): Nudge | undefined {
  if (!me) return undefined;
  return NUDGES.find((n) => !quiet(n, pathname) && !snoozed(heldAnswer(n, preferences.nudges[n.id]), session) && n.applies(me, facts));
}

/** A "never" stored against an ask that may not be refused (written by hand, or before the entry became non-dismissible) does not hide it. */
function heldAnswer(n: Nudge, state: NudgeState | undefined): NudgeState | undefined {
  return state?.state === "never" && !n.dismissible ? undefined : state;
}
